import React, { useState, useEffect, useCallback, useRef, forwardRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { sampleBooks } from '../data/mock';
import HTMLFlipBook from 'react-pageflip';
import {
  ChevronLeft,
  ChevronRight,
  Bookmark,
  BookmarkCheck,
  Home,
  Menu,
  BookOpen,
  Library,
  LogOut,
  Loader2,
  Pencil,
  Eraser,
  Maximize2,
  Minimize2,
  Type
} from 'lucide-react';
import { toast } from 'sonner';

// Import API URL from utility
import { API_URL, getImageUrl } from '../lib/api';

// Drawing Canvas Component - Champion only feature
// Uses a SEPARATE overlay approach to avoid transform issues from react-pageflip
const DrawingOverlay = ({ 
  isActive,
  isTextMode,
  brushColor, 
  brushSize, 
  bookId, 
  currentPage,
  totalPages,
  bookContainerRef,
  clearTrigger
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const [textInput, setTextInput] = useState(null); // { x, y, text }
  
  const storageKey = `book_drawings_${bookId}`;

  // Initialize canvas and load drawings
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = bookContainerRef?.current;
    if (!canvas || !container) return;

    const updateCanvasSize = () => {
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      
      // Reload drawing after resize
      loadDrawing();
    };

    const loadDrawing = () => {
      try {
        const savedDrawings = localStorage.getItem(storageKey);
        if (savedDrawings) {
          const drawings = JSON.parse(savedDrawings);
          const pageKey = currentPage + 1;
          if (drawings[pageKey]) {
            const img = new window.Image();
            img.onload = () => {
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              }
            };
            img.src = drawings[pageKey];
          }
        }
      } catch (e) {
        console.error('Failed to load drawing:', e);
      }
    };

    updateCanvasSize();
    
    const timers = [
      setTimeout(updateCanvasSize, 100),
      setTimeout(updateCanvasSize, 300),
      setTimeout(updateCanvasSize, 600)
    ];

    window.addEventListener('resize', updateCanvasSize);
    
    return () => {
      timers.forEach(t => clearTimeout(t));
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [bookContainerRef, storageKey, currentPage]);

  // Clear canvas and reload when page changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Load drawing for new page
    try {
      const savedDrawings = localStorage.getItem(storageKey);
      if (savedDrawings) {
        const drawings = JSON.parse(savedDrawings);
        const pageKey = currentPage + 1;
        if (drawings[pageKey]) {
          const img = new window.Image();
          img.onload = () => {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          };
          img.src = drawings[pageKey];
        }
      }
    } catch (e) {
      console.error('Failed to load drawing:', e);
    }
  }, [currentPage, storageKey]);

  // Handle clear trigger from parent
  useEffect(() => {
    if (clearTrigger > 0) {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }
    }
  }, [clearTrigger]);

  // Get pointer position - direct calculation, no transforms
  const getPointerPos = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }, []);

  const saveDrawing = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bookId) return;
    
    try {
      const dataUrl = canvas.toDataURL('image/png');
      const savedDrawings = JSON.parse(localStorage.getItem(storageKey) || '{}');
      const pageKey = currentPage + 1;
      savedDrawings[pageKey] = dataUrl;
      localStorage.setItem(storageKey, JSON.stringify(savedDrawings));
    } catch (e) {
      console.error('Failed to save drawing:', e);
    }
  }, [bookId, currentPage, storageKey]);

  const commitTextToCanvas = useCallback((text, x, y) => {
    const canvas = canvasRef.current;
    if (!canvas || !text) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const fontSize = Math.max(14, brushSize * 4);
    ctx.font = `${fontSize}px Georgia, serif`;
    ctx.fillStyle = brushColor;
    ctx.fillText(text, x, y + fontSize);
    saveDrawing();
  }, [brushColor, brushSize, saveDrawing]);

  const handlePointerDown = useCallback((e) => {
    if (!isActive) return;
    e.preventDefault();
    e.stopPropagation();
    
    const pos = getPointerPos(e);
    
    if (isTextMode) {
      // Commit any existing text first
      if (textInput?.text) {
        commitTextToCanvas(textInput.text, textInput.x, textInput.y);
      }
      setTextInput({ x: pos.x, y: pos.y, text: '' });
      return;
    }
    
    lastPosRef.current = pos;
    setIsDrawing(true);
  }, [isActive, isTextMode, getPointerPos, textInput, commitTextToCanvas]);

  const handlePointerMove = useCallback((e) => {
    if (!isDrawing || !isActive) return;
    e.preventDefault();
    e.stopPropagation();
    
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    const pos = getPointerPos(e);
    
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = brushColor;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    
    lastPosRef.current = pos;
  }, [isDrawing, isActive, brushColor, brushSize, getPointerPos]);

  const handlePointerUp = useCallback((e) => {
    if (isDrawing) {
      saveDrawing();
    }
    setIsDrawing(false);
  }, [isDrawing, saveDrawing]);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{
          zIndex: isActive ? 100 : 5,
          pointerEvents: isActive ? 'auto' : 'none',
          cursor: isActive ? (isTextMode ? 'text' : 'crosshair') : 'default',
          touchAction: 'none'
        }}
        onMouseDown={handlePointerDown}
        onMouseMove={handlePointerMove}
        onMouseUp={handlePointerUp}
        onMouseLeave={handlePointerUp}
        onTouchStart={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerUp}
      />
      {/* Text input overlay */}
      {textInput && isTextMode && isActive && (
        <input
          autoFocus
          type="text"
          value={textInput.text}
          onChange={(e) => setTextInput({ ...textInput, text: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commitTextToCanvas(textInput.text, textInput.x, textInput.y);
              setTextInput(null);
            }
            if (e.key === 'Escape') setTextInput(null);
          }}
          onBlur={() => {
            if (textInput?.text) commitTextToCanvas(textInput.text, textInput.x, textInput.y);
            setTextInput(null);
          }}
          className="absolute border-2 border-blue-500 bg-white/80 px-1 py-0.5 text-sm outline-none rounded"
          style={{
            left: textInput.x,
            top: textInput.y,
            zIndex: 200,
            color: brushColor,
            fontFamily: 'Georgia, serif',
            fontSize: `${Math.max(14, brushSize * 4)}px`,
            minWidth: '100px'
          }}
          placeholder="Type here..."
        />
      )}
    </>
  );
};

// Simple Page component
const Page = forwardRef(({ number, image, isCover, children }, ref) => {
  return (
    <div 
      ref={ref} 
      className="page bg-amber-50 relative overflow-hidden"
      data-density={isCover ? "hard" : "soft"}
    >
      {image ? (
        <img
          src={image}
          alt={`Page ${number}`}
          className="w-full h-full object-contain pointer-events-none select-none"
          draggable={false}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-amber-50">
          {children}
        </div>
      )}
    </div>
  );
});

Page.displayName = 'Page';

const FlipbookReader = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Book state
  const [book, setBook] = useState(null);
  const [pageImages, setPageImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Bookmarks - Adventurer and Champion
  const [bookmarks, setBookmarks] = useState([]);
  
  // Drawing state - Champion only
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [isTextMode, setIsTextMode] = useState(false);
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(4);
  const [clearTrigger, setClearTrigger] = useState(0);
  const brushColors = ['#000000', '#ef4444', '#22c55e', '#3b82f6', '#eab308', '#a855f7', '#ec4899'];
  
  // Expand mode
  const [isExpanded, setIsExpanded] = useState(false);
  
  const bookRef = useRef(null);
  const containerRef = useRef(null);
  const bookContainerRef = useRef(null);
  
  // Book dimensions - single source of truth
  const [bookDimensions, setBookDimensions] = useState({ width: 450, height: 540 });

  // Tier-based permissions
  const userTier = user?.tier || 'free';
  const canDraw = userTier === 'premium' || userTier === 'faith_builder';
  const canBookmark = userTier === 'basic' || userTier === 'premium' || userTier === 'faith_builder';

  // Load book data
  useEffect(() => {
    const loadBook = async () => {
      setLoading(true);
      
      // Always try API first to get the most up-to-date pages
      try {
        const response = await fetch(`${API_URL}/api/books/${bookId}`);
        if (response.ok) {
          const data = await response.json();
          
          // Check if API has pages
          const pages = data.pages || [];
          const pageUrls = pages.map(p => {
            if (typeof p === 'string') return getImageUrl(p);
            if (p && p.image_url) return getImageUrl(p.image_url);
            return null;
          }).filter(Boolean);
          
          if (pageUrls.length > 0) {
            // Use API data - it has pages
            setBook(data);
            setPageImages(pageUrls);
            setTotalPages(pageUrls.length);
            setLoading(false);
            console.log(`Loaded ${pageUrls.length} pages from API for: ${data.title}`);
            return;
          }
        }
      } catch (error) {
        console.log('API fetch failed, trying sample data:', error);
      }
      
      // Fallback to sample books if API doesn't have the book or has no pages
      const sampleBook = sampleBooks.find(b => b.id === bookId);
      
      if (sampleBook) {
        setBook(sampleBook);
        const images = sampleBook.pageImages?.filter(Boolean) || [];
        setPageImages(images);
        setTotalPages(images.length);
        console.log(`Loaded ${images.length} pages from sample data for: ${sampleBook.title}`);
        setLoading(false);
        return;
      }
      
      // Book not found anywhere
      toast.error('Book not found');
      navigate('/dashboard');
      setLoading(false);
    };
    
    if (bookId) loadBook();
  }, [bookId, navigate]);

  // Track initial book open as a page turn
  useEffect(() => {
    if (!loading && pageImages.length > 0) {
      const token = localStorage.getItem('session_token');
      if (token) {
        fetch(`${API_URL}/api/user/stats/page-turn`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        }).catch(() => {});
      }
      
      // Resume reading from last position
      if (user?.user_id && bookId) {
        const progressKey = `reading_progress_${user.user_id}_${bookId}`;
        const saved = localStorage.getItem(progressKey);
        if (saved) {
          try {
            const data = JSON.parse(saved);
            const savedPage = data.currentPage || 0;
            // Convert from 1-based saved to 0-based flip index (even number for double spread)
            const flipTo = Math.max(0, Math.min(savedPage - 2, pageImages.length - 2));
            const flipIndex = flipTo % 2 === 0 ? flipTo : flipTo - 1; // Ensure even index
            if (flipIndex > 0) {
              // Small delay to let the book render first
              setTimeout(() => {
                bookRef.current?.pageFlip()?.flip(flipIndex, 'top');
                setCurrentPage(flipIndex);
              }, 500);
            }
          } catch (e) { /* ignore parse errors */ }
        }
      }
    }
  }, [loading, pageImages.length, user?.user_id, bookId]);

  // Load bookmarks from localStorage
  useEffect(() => {
    if (bookId && user?.user_id) {
      const savedBookmarks = localStorage.getItem(`bookmarks_${user.user_id}_${bookId}`);
      if (savedBookmarks) {
        setBookmarks(JSON.parse(savedBookmarks));
      }
    }
  }, [bookId, user?.user_id]);

  // Detect mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate book dimensions based on viewport and expand state
  useEffect(() => {
    const calculateDimensions = () => {
      const container = containerRef.current;
      if (!container) return;
      
      const containerWidth = container.clientWidth;
      const containerHeight = window.innerHeight - 100;
      const mobile = containerWidth < 640;
      
      // Aspect ratio for children's book
      const aspectRatio = 0.83;
      
      let width, height;
      
      if (mobile) {
        // Mobile: single page fills the screen width
        width = containerWidth * 0.95;
        height = width / aspectRatio;
        if (height > containerHeight * 0.8) {
          height = containerHeight * 0.8;
          width = height * aspectRatio;
        }
      } else if (isExpanded) {
        width = containerWidth * 0.65;
        height = width / aspectRatio;
        if (height > containerHeight) {
          height = containerHeight;
          width = height * aspectRatio;
        }
        width = Math.max(width, Math.min(500, containerWidth * 0.9));
        height = Math.max(height, width / aspectRatio);
      } else {
        width = containerWidth * 0.35;
        height = width / aspectRatio;
        if (height > containerHeight * 0.75) {
          height = containerHeight * 0.75;
          width = height * aspectRatio;
        }
        width = Math.max(width, 320);
        height = Math.max(height, 385);
        width = Math.min(width, 420);
        height = Math.min(height, 505);
      }
      
      setBookDimensions({ width: Math.floor(width), height: Math.floor(height) });
    };
    
    calculateDimensions();
    window.addEventListener('resize', calculateDimensions);
    return () => window.removeEventListener('resize', calculateDimensions);
  }, [isExpanded]);

  // Handle page flip
  const onFlip = useCallback((e) => {
    const newPage = e.data;
    setCurrentPage(newPage);
    
    // Save progress — on mobile (portrait/single page), each flip = 1 page; on desktop (spread), each flip = 2 pages
    const pagesReached = isMobile ? Math.min(newPage + 1, pageImages.length) : Math.min(newPage + 2, pageImages.length);
    
    if (user?.user_id && bookId) {
      const progressKey = `reading_progress_${user.user_id}_${bookId}`;
      localStorage.setItem(progressKey, JSON.stringify({
        currentPage: pagesReached,
        lastRead: new Date().toISOString()
      }));
    }
    
    const token = localStorage.getItem('session_token');
    if (token) {
      fetch(`${API_URL}/api/user/stats/page-turn`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      }).catch(err => console.log('Page turn tracking failed:', err));
    }
    
    // Book complete — reached last spread
    if (pagesReached >= pageImages.length && token) {
      const completedBooks = JSON.parse(localStorage.getItem('completedBooks') || '[]');
      if (!completedBooks.includes(bookId)) {
        fetch(`${API_URL}/api/user/stats/book-complete`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        }).then(() => {
          completedBooks.push(bookId);
          localStorage.setItem('completedBooks', JSON.stringify(completedBooks));
          toast.success('Congratulations! You finished the book!');
        }).catch(err => console.log('Book complete tracking failed:', err));
      }
    }
  }, [bookId, pageImages.length, user?.user_id]);

  // Navigation
  const nextPage = useCallback(() => {
    if (isDrawingMode || isTextMode) {
      toast.info('Turn off drawing to flip pages');
      return;
    }
    bookRef.current?.pageFlip()?.flipNext('top');
  }, [isDrawingMode]);

  const prevPage = useCallback(() => {
    if (isDrawingMode || isTextMode) {
      toast.info('Turn off drawing to flip pages');
      return;
    }
    bookRef.current?.pageFlip()?.flipPrev('top');
  }, [isDrawingMode]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isDrawingMode || isTextMode) return;
      
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextPage();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevPage();
      } else if (e.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextPage, prevPage, isDrawingMode, isExpanded]);

  // Toggle bookmark
  const toggleBookmark = () => {
    if (!canBookmark) {
      toast.error('Upgrade to Adventurer to bookmark pages!');
      return;
    }
    
    const isBookmarked = bookmarks.includes(currentPage);
    let newBookmarks;
    
    if (isBookmarked) {
      newBookmarks = bookmarks.filter(b => b !== currentPage);
      toast.info('Bookmark removed');
    } else {
      newBookmarks = [...bookmarks, currentPage];
      toast.success('Page bookmarked!');
    }
    
    setBookmarks(newBookmarks);
    if (user?.user_id) {
      localStorage.setItem(`bookmarks_${user.user_id}_${bookId}`, JSON.stringify(newBookmarks));
    }
  };

  // Clear drawing - FIXED: actually clears the canvas
  const clearDrawing = () => {
    const storageKey = `book_drawings_${bookId}`;
    const savedDrawings = JSON.parse(localStorage.getItem(storageKey) || '{}');
    const pageKey = currentPage + 1;
    delete savedDrawings[pageKey];
    localStorage.setItem(storageKey, JSON.stringify(savedDrawings));
    
    // Trigger canvas clear in DrawingOverlay
    setClearTrigger(prev => prev + 1);
    
    toast.success('Drawing cleared!');
  };

  const isCurrentPageBookmarked = bookmarks.includes(currentPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 via-emerald-50 to-sky-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading your book...</p>
        </div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 via-emerald-50 to-sky-50 flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-serif font-bold text-slate-800 mb-2">Book Not Found</h2>
          <p className="text-slate-600 mb-6">This book doesn't seem to exist.</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-emerald-500 hover:bg-emerald-600">
            Return to Library
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`min-h-screen flex flex-col bg-gradient-to-b from-amber-50 via-emerald-50 to-sky-50 ${isExpanded ? 'fixed inset-0 z-50 overflow-auto' : ''}`}
    >
      {/* Compact Header */}
      <header className="bg-white/90 backdrop-blur-sm shadow-sm z-40 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 h-12 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-slate-600 hover:text-emerald-600 h-8"
            >
              <Home className="w-4 h-4 mr-1" />
              Library
            </Button>
            
            <div className="hidden sm:block border-l border-slate-200 pl-4">
              <h1 className="font-serif font-bold text-slate-800 truncate max-w-[180px]">
                {book.title}
              </h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <BookOpen className="w-4 h-4" />
            <span>{currentPage + 1} / {totalPages}</span>
          </div>
          
          <div className="flex items-center gap-1">
            {/* Drawing tools - Champion only */}
            {canDraw && (
              <div className="flex items-center gap-1">
                {isDrawingMode && (
                  <>
                    <div className="hidden sm:flex gap-0.5 mr-1">
                      {brushColors.map(color => (
                        <button
                          key={color}
                          onClick={() => setBrushColor(color)}
                          className={`w-4 h-4 rounded-full border-2 ${
                            brushColor === color ? 'border-slate-800 scale-110' : 'border-transparent'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    
                    <div className="hidden sm:flex items-center gap-0.5 bg-slate-100 rounded px-1">
                      <button onClick={() => setBrushSize(Math.max(1, brushSize - 1))} className="text-xs font-bold px-1">-</button>
                      <span className="text-xs w-3 text-center">{brushSize}</span>
                      <button onClick={() => setBrushSize(Math.min(15, brushSize + 1))} className="text-xs font-bold px-1">+</button>
                    </div>
                    
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={clearDrawing} 
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      title="Clear drawing on this page"
                    >
                      <Eraser className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const newMode = !isDrawingMode;
                    setIsDrawingMode(newMode);
                    if (newMode) {
                      setIsTextMode(false);
                      toast.info('Draw on the page!');
                    } else {
                      toast.success('Drawing saved!');
                    }
                  }}
                  className={`h-7 w-7 p-0 ${isDrawingMode ? 'bg-purple-500 text-white' : ''}`}
                  data-testid="draw-mode-btn"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const newMode = !isTextMode;
                    setIsTextMode(newMode);
                    if (newMode) {
                      setIsDrawingMode(false);
                      toast.info('Click anywhere on the page to type!');
                    }
                  }}
                  className={`h-7 w-7 p-0 ${isTextMode ? 'bg-blue-500 text-white' : ''}`}
                  data-testid="text-mode-btn"
                >
                  <Type className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className={`h-7 w-7 p-0 ${isExpanded ? 'bg-emerald-500 text-white' : ''}`}
              data-testid="expand-btn"
              title={isExpanded ? 'Normal size' : 'Expand by 70%'}
            >
              {isExpanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            </Button>
            
            {canBookmark && (
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleBookmark}
                className={`h-7 w-7 p-0 ${isCurrentPageBookmarked ? 'text-coral-500' : ''}`}
                data-testid="bookmark-btn"
              >
                {isCurrentPageBookmarked ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
              </Button>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="sm:hidden h-7 w-7 p-0"
            >
              <Menu className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        
        {isMobileMenuOpen && (
          <div className="sm:hidden bg-white border-t px-4 py-2 space-y-1">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-600 py-1 text-sm" onClick={() => setIsMobileMenuOpen(false)}>
              <Library className="w-4 h-4" />
              My Library
            </Link>
            {user && (
              <button onClick={() => { logout(); setIsMobileMenuOpen(false); }} className="flex items-center gap-2 text-slate-600 py-1 w-full text-sm">
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            )}
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center p-2">
        {(isDrawingMode || isTextMode) && (
          <div className="mb-1 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
            {isDrawingMode ? 'Drawing mode' : 'Text mode'} — Page turning disabled
          </div>
        )}
        
        <div className="flex items-center gap-2 w-full justify-center flex-1">
          {/* Previous Button */}
          <Button
            variant="ghost"
            size="lg"
            onClick={prevPage}
            disabled={currentPage === 0 || isDrawingMode || isTextMode}
            className={`hidden sm:flex w-10 h-10 rounded-full shadow-lg bg-white/80 hover:bg-emerald-50 ${isDrawingMode ? 'opacity-30' : ''}`}
            data-testid="prev-page-btn"
          >
            <ChevronLeft className="w-6 h-6 text-emerald-600" />
          </Button>

          {/* Flipbook Container */}
          <div ref={bookContainerRef} className="relative">
            <div className="absolute inset-x-8 bottom-0 h-6 bg-black/10 blur-xl rounded-full" />
            
            {/* Drawing overlay - positioned OVER the flipbook */}
            {canDraw && (
              <DrawingOverlay
                isActive={isDrawingMode || isTextMode}
                isTextMode={isTextMode}
                brushColor={brushColor}
                brushSize={brushSize}
                bookId={bookId}
                currentPage={currentPage}
                totalPages={totalPages}
                bookContainerRef={bookContainerRef}
                clearTrigger={clearTrigger}
              />
            )}
            
            <HTMLFlipBook
              key={`flipbook-${isExpanded ? 'expanded' : 'normal'}-${isMobile ? 'mobile' : 'desktop'}`}
              ref={bookRef}
              width={bookDimensions.width}
              height={bookDimensions.height}
              size="fixed"
              minWidth={200}
              maxWidth={1200}
              minHeight={260}
              maxHeight={1400}
              showCover={true}
              mobileScrollSupport={false}
              onFlip={onFlip}
              className="shadow-2xl"
              style={{ margin: '0 auto' }}
              maxShadowOpacity={isMobile ? 0.2 : 0.4}
              drawShadow={true}
              flippingTime={isMobile ? 400 : 500}
              usePortrait={isMobile}
              startPage={currentPage}
              swipeDistance={30}
              autoSize={false}
              clickEventForward={false}
              useMouseEvents={!isDrawingMode || isTextMode}
              swipeDistance={isDrawingMode ? 99999 : 30}
              disableFlipByClick={isDrawingMode || isTextMode}
            >
              {pageImages.map((image, index) => (
                <Page
                  key={index}
                  number={index + 1}
                  image={image}
                  isCover={index === 0}
                />
              ))}
            </HTMLFlipBook>
            
            {isCurrentPageBookmarked && (
              <div className="absolute top-0 right-4 w-6 h-10 bg-coral-500 shadow-lg z-10" style={{
                clipPath: 'polygon(0 0, 100% 0, 100% 100%, 50% 80%, 0 100%)'
              }}>
                <Bookmark className="w-3 h-3 text-white mx-auto mt-1" />
              </div>
            )}
          </div>

          {/* Next Button */}
          <Button
            variant="ghost"
            size="lg"
            onClick={nextPage}
            disabled={currentPage >= pageImages.length - 1 || isDrawingMode || isTextMode}
            className={`hidden sm:flex w-10 h-10 rounded-full shadow-lg bg-white/80 hover:bg-emerald-50 ${isDrawingMode ? 'opacity-30' : ''}`}
            data-testid="next-page-btn"
          >
            <ChevronRight className="w-6 h-6 text-emerald-600" />
          </Button>
        </div>
      </main>

      {/* Mobile Navigation */}
      <div className="flex sm:hidden items-center justify-center gap-6 py-2 flex-shrink-0">
        <Button
          variant="ghost"
          size="lg"
          onClick={prevPage}
          disabled={currentPage === 0 || isDrawingMode || isTextMode}
          className={`w-10 h-10 rounded-full shadow-lg bg-white ${isDrawingMode ? 'opacity-30' : ''}`}
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        
        <span className="font-medium text-slate-600 text-sm">{isMobile ? currentPage + 1 : Math.min(currentPage + 2, pageImages.length)} / {pageImages.length}</span>
        
        <Button
          variant="ghost"
          size="lg"
          onClick={nextPage}
          disabled={currentPage >= pageImages.length - 1 || isDrawingMode || isTextMode}
          className={`w-10 h-10 rounded-full shadow-lg bg-white ${isDrawingMode ? 'opacity-30' : ''}`}
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Page indicators */}
      <div className="hidden sm:flex items-center justify-center gap-1 pb-2 flex-shrink-0">
        {pageImages.slice(0, Math.min(20, pageImages.length)).map((_, idx) => (
          <button
            key={idx}
            onClick={() => !isDrawingMode && bookRef.current?.pageFlip()?.flip(idx, 'top')}
            disabled={isDrawingMode || isTextMode}
            className={`h-1.5 rounded-full transition-all ${
              idx === currentPage
                ? 'bg-emerald-500 w-6'
                : bookmarks.includes(idx)
                  ? 'bg-coral-400 w-1.5'
                  : 'bg-slate-300 hover:bg-emerald-300 w-1.5'
            } ${isDrawingMode ? 'opacity-50' : ''}`}
          />
        ))}
        {pageImages.length > 20 && <span className="text-xs ml-1 text-slate-500">+{pageImages.length - 20}</span>}
      </div>
    </div>
  );
};

export default FlipbookReader;
